import {
    Users,
    Building,
    Crown,
    UserCog,
    MapPin,
    Package,
    Headphones,
    Phone,
    Shield,
    Mail,
    Calendar,
    CheckCircle,
    XCircle,
    Eye,
    EyeOff,
    Plus,
    Edit,
    Trash2,
    Save,
    X,
    AlertCircle,
    Globe,
} from "lucide-react";

export interface User {
    id: string;
    username: string;
    email: string;
    role: string;
    countries: string[];
    permissions: string[];
    isActive: boolean;
    linkedAccountId?: number; // Alter CPA account ID
    teamId?: string; // Team membership
    isSupervisor?: boolean; // Is supervisor of a team
    achievements?: string[]; // Task achievements for call operators (e.g., ['update_address', 'sell_new_product'])
    profile: {
        firstName: string;
        lastName: string;
        phone: string;
        department: string;
    };
    createdAt: string;
    lastLogin: string | null;
}

export interface Team {
    id: string;
    name: string;
    type: string;
    description: string;
    countries: string[];
    supervisorId: string;
    members: string[];
    createdAt: string;
    updatedAt: string;
}

export interface UserStats {
    totalUsers: number;
    activeUsers: number;
    inactiveUsers: number;
    totalTeams: number;
    usersByRole: Record<string, number>;
    usersByCountry: Record<string, number>;
}

export interface FilterState {
    searchTerm: string;
    selectedRole: string;
    selectedCountry: string;
    selectedTeam: string;
    showActiveOnly: boolean;
}

export type TabType = "users" | "teams" | "hierarchy" | "stats";

export interface UserFormData {
    username: string;
    email: string;
    password: string;
    role: string;
    countries: string[];
    permissions: string[];
    linkedAccountId?: string; // Optional - AlterCPA removed for Serbia-CC
    profile: {
        firstName: string;
        lastName: string;
        phone: string;
        department: string;
    };
}

export interface TeamFormData {
    name: string;
    type: string;
    description: string;
    countries: string[];
    supervisorId: string;
    members: string[];
}

// Role configuration
export const roleConfig = {
    admin: {
        icon: Crown,
        color: "text-purple-600",
        bg: "bg-purple-100",
    },
    manager: {
        icon: UserCog,
        color: "text-blue-600",
        bg: "bg-blue-100",
    },
    "country-manager": {
        icon: MapPin,
        color: "text-green-600",
        bg: "bg-green-100",
    },
    "country-post-manager": {
        icon: Package,
        color: "text-indigo-600",
        bg: "bg-indigo-100",
    },
    "call-center": {
        icon: Headphones,
        color: "text-orange-600",
        bg: "bg-orange-100",
    },
    "call-operator": {
        icon: Phone,
        color: "text-blue-600",
        bg: "bg-blue-100",
    },
    "post-operator": {
        icon: Package,
        color: "text-purple-600",
        bg: "bg-purple-100",
    },
    "website-post-manager": {
        icon: Globe,
        color: "text-teal-600",
        bg: "bg-teal-100",
    },
};

// Helper functions
export const getRoleIcon = (role: string) => {
    return roleConfig[role as keyof typeof roleConfig]?.icon || Users;
};

export const getRoleColor = (role: string) => {
    return roleConfig[role as keyof typeof roleConfig]?.color || "text-gray-600";
};

export const getRoleBg = (role: string) => {
    return roleConfig[role as keyof typeof roleConfig]?.bg || "bg-gray-100";
};

export const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
};

export const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
};

export const getStatusIcon = (isActive: boolean) => {
    return isActive ? CheckCircle : XCircle;
};

export const getStatusColor = (isActive: boolean) => {
    return isActive ? "text-green-600" : "text-red-600";
};

export const getStatusBg = (isActive: boolean) => {
    return isActive ? "bg-green-100" : "bg-red-100";
};
